"use strict";
import { createSlice } from "@reduxjs/toolkit";
import {
  clampAllocation,
  distributeAllocationAmount,
  remainingAllocation,
  splitAllocationEvenly,
  sumAllocations
} from "state/voting/allocation";
const initialState = {
  selectedLock: null,
  availableGauges: [],
  filteredGauges: [],
  currentVotes: [],
  pendingVotes: [],
  totalAllocatedWeight: 0,
  totalAllocatedPercentage: 0,
  epochInfo: null,
  searchTerm: "",
  sortBy: "reserveUSD",
  sortDirection: "desc",
  loading: {
    gauges: false,
    userVotes: false,
    transaction: false
  },
  error: null
};
function setVoteAllocation(vote, percentage) {
  const clamped = clampAllocation(percentage);
  vote.weight = clamped;
  vote.percentage = clamped;
}
function applyEvenSplit(votes) {
  const split = splitAllocationEvenly(votes.length);
  votes.forEach((vote, index) => {
    setVoteAllocation(vote, split[index] ?? 0);
  });
}
function totalAllocation(votes) {
  return sumAllocations(votes.map((vote) => vote.percentage));
}
export const votingSlice = createSlice({
  name: "voting",
  initialState,
  reducers: {
    // Lock selection. The pool basket is intentionally lock-independent: allocations are shares of
    // whichever lock is selected, so switching locks must not discard the user's pool selection.
    setSelectedLock: (state, action) => {
      state.selectedLock = action.payload;
    },
    // Gauge data
    setAvailableGauges: (state, action) => {
      state.availableGauges = action.payload;
      state.filteredGauges = action.payload;
    },
    setFilteredGauges: (state, action) => {
      state.filteredGauges = action.payload;
    },
    // Current votes
    setCurrentVotes: (state, action) => {
      state.currentVotes = action.payload;
    },
    // Pending votes management
    addPendingVote: (state, action) => {
      const { poolAddress, poolId, pool } = action.payload;
      const existing = state.pendingVotes.find((v) => v.poolAddress === poolAddress);
      if (existing) {
        existing.poolId = poolId;
        if (pool) {
          existing.pool = pool;
        }
      } else {
        state.pendingVotes.push({
          poolAddress,
          poolId,
          weight: 0,
          percentage: 0,
          pool
        });
      }
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    removePendingVote: (state, action) => {
      const poolAddress = action.payload;
      state.pendingVotes = state.pendingVotes.filter((v) => v.poolAddress !== poolAddress);
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    // A zero weight keeps the pool selected at 0% — clearing the percentage field must never
    // deselect the pool. Only removePendingVote takes a pool off the list.
    updatePendingVoteWeight: (state, action) => {
      const { poolAddress, weight } = action.payload;
      const vote = state.pendingVotes.find((v) => v.poolAddress === poolAddress);
      if (vote) {
        setVoteAllocation(vote, weight);
        votingSlice.caseReducers.recalculatePercentages(state);
      }
    },
    /** Whole-number even split across every selected pool: 3 pools -> 34/33/33. */
    splitPendingVotesEvenly: (state) => {
      applyEvenSplit(state.pendingVotes);
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    /**
     * Hands the unallocated remainder to the pools sitting at 0%, or spreads it across every pool
     * when they all already hold an allocation.
     */
    distributeRemainingAllocation: (state) => {
      const remaining = remainingAllocation(totalAllocation(state.pendingVotes));
      if (remaining <= 0 || state.pendingVotes.length === 0) {
        return;
      }
      const unallocated = state.pendingVotes.filter((vote) => vote.percentage <= 0);
      const targets = unallocated.length > 0 ? unallocated : state.pendingVotes;
      const shares = distributeAllocationAmount(remaining, targets.length);
      targets.forEach((vote, index) => {
        setVoteAllocation(vote, vote.percentage + (shares[index] ?? 0));
      });
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    /** Grows one pool by whatever is still unallocated. */
    maxPendingVoteAllocation: (state, action) => {
      const remaining = remainingAllocation(totalAllocation(state.pendingVotes));
      const vote = state.pendingVotes.find((v) => v.poolAddress === action.payload);
      if (vote && remaining > 0) {
        setVoteAllocation(vote, vote.percentage + remaining);
        votingSlice.caseReducers.recalculatePercentages(state);
      }
    },
    /**
     * Refreshes each selected pool's gauge snapshot from the live gauge list and drops pools that
     * are no longer offered (killed gauge, or a basket restored on a different chain). Ignores empty
     * gauge lists so a pending fetch never wipes the basket.
     */
    syncPendingVotePools: (state, action) => {
      const gauges = action.payload;
      if (gauges.length === 0 || state.pendingVotes.length === 0) {
        return;
      }
      const gaugesByPool = new Map(gauges.map((gauge) => [gauge.poolId.toLowerCase(), gauge]));
      const survivors = [];
      state.pendingVotes.forEach((vote) => {
        const gauge = gaugesByPool.get(vote.poolAddress.toLowerCase());
        if (!gauge) {
          return;
        }
        survivors.push({ ...vote, poolId: gauge.id, pool: gauge });
      });
      state.pendingVotes = survivors;
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    /** Rehydrates a basket saved earlier in the session; gauge snapshots arrive via syncPendingVotePools. */
    restorePendingVotes: (state, action) => {
      state.pendingVotes = action.payload.map((vote) => ({
        poolAddress: vote.poolAddress,
        poolId: vote.poolId,
        weight: clampAllocation(vote.percentage),
        percentage: clampAllocation(vote.percentage)
      }));
      votingSlice.caseReducers.recalculatePercentages(state);
    },
    clearPendingVotes: (state) => {
      state.pendingVotes = [];
      state.totalAllocatedWeight = 0;
      state.totalAllocatedPercentage = 0;
    },
    // Allocations are what the user entered — they are never normalised here. Voter.vote() does its
    // own normalisation against the sum of the submitted weights.
    recalculatePercentages: (state) => {
      state.totalAllocatedWeight = sumAllocations(state.pendingVotes.map((vote) => vote.weight));
      state.totalAllocatedPercentage = totalAllocation(state.pendingVotes);
    },
    // Epoch information
    setEpochInfo: (state, action) => {
      state.epochInfo = action.payload;
    },
    // UI state
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSortBy: (state, action) => {
      if (state.sortBy === action.payload) {
        state.sortDirection = state.sortDirection === "desc" ? "asc" : "desc";
      } else {
        state.sortBy = action.payload;
        state.sortDirection = "desc";
      }
    },
    // Loading states
    setGaugesLoading: (state, action) => {
      state.loading.gauges = action.payload;
    },
    setUserVotesLoading: (state, action) => {
      state.loading.userVotes = action.payload;
    },
    setTransactionLoading: (state, action) => {
      state.loading.transaction = action.payload;
    },
    // Error handling
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Reset all state
    resetVotingState: (state) => {
      Object.assign(state, initialState);
    }
  }
});
export const {
  setSelectedLock,
  setAvailableGauges,
  setFilteredGauges,
  setCurrentVotes,
  addPendingVote,
  removePendingVote,
  updatePendingVoteWeight,
  splitPendingVotesEvenly,
  distributeRemainingAllocation,
  maxPendingVoteAllocation,
  syncPendingVotePools,
  restorePendingVotes,
  clearPendingVotes,
  recalculatePercentages,
  setEpochInfo,
  setSearchTerm,
  setSortBy,
  setGaugesLoading,
  setUserVotesLoading,
  setTransactionLoading,
  setError,
  clearError,
  resetVotingState
} = votingSlice.actions;
export default votingSlice.reducer;
export const selectVotingState = (state) => state.voting;
export const selectSelectedLock = (state) => state.voting.selectedLock;
export const selectAvailableGauges = (state) => state.voting.availableGauges;
export const selectFilteredGauges = (state) => state.voting.filteredGauges;
export const selectPendingVotes = (state) => state.voting.pendingVotes;
export const selectTotalAllocatedPercentage = (state) => state.voting.totalAllocatedPercentage;
export const selectCurrentVotes = (state) => state.voting.currentVotes;
export const selectEpochInfo = (state) => state.voting.epochInfo;
export const selectVotingLoading = (state) => state.voting.loading;
export const selectVotingError = (state) => state.voting.error;
export const selectCanVote = (state) => {
  const { selectedLock, epochInfo } = state.voting;
  return !!(selectedLock && epochInfo && !epochInfo.isDistributionPeriod && Date.now() / 1e3 < epochInfo.epochVoteEnd);
};
export const selectHasPendingVotes = (state) => {
  return state.voting.pendingVotes.length > 0;
};
export const selectVotingPowerUtilization = (state) => {
  const { selectedLock, totalAllocatedPercentage } = state.voting;
  if (!selectedLock) return 0;
  return totalAllocatedPercentage;
};
